// 04 — one row of the capture browser matrix
// (packages/speech/src/capture/README.md § Browser matrix), filled from this
// device and the most recent kept recording, ready to paste into that table.
import { Button } from "@bindery/ui/components/ui/button";
import { Input } from "@bindery/ui/components/ui/input";
import { Label } from "@bindery/ui/components/ui/label";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import {
  bytesPerMinute,
  type DeviceReport,
  type Reprompt,
  reportText,
} from "../lib/device-report";
import { bytes } from "../lib/format";
import { Absent, Panel, Readout, Readouts } from "./panel";

type CopyState = "idle" | "copied" | "failed";

const REPROMPTS: readonly { value: Reprompt; label: string }[] = [
  { value: "untested", label: "untested" },
  { value: "yes", label: "yes — prompt came back" },
  { value: "no", label: "no — straight back to blocked" },
];

const isReprompt = (value: string): value is Reprompt =>
  REPROMPTS.some((r) => r.value === value);

export function DeviceReportPanel({ report }: { report: DeviceReport }) {
  const [reprompt, setReprompt] = useState<Reprompt>("untested");
  const [notes, setNotes] = useState("");
  const [copy, setCopy] = useState<CopyState>("idle");
  const text = reportText(report, reprompt, notes);
  const { capability, recording, browser } = report;
  const bpm = recording === null ? null : bytesPerMinute(recording);

  const onCopy = () => {
    navigator.clipboard.writeText(text).then(
      () => setCopy("copied"),
      () => setCopy("failed"),
    );
  };

  return (
    <Panel
      index="04"
      title="Device report"
      aside={
        <Button size="xs" variant="outline" onClick={onCopy}>
          {copy === "copied" ? <Check /> : <Copy />}
          {copy === "copied"
            ? "Copied"
            : copy === "failed"
              ? "Copy failed — select below"
              : "Copy for the matrix"}
        </Button>
      }
    >
      <div className="grid gap-x-6 gap-y-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Readouts>
          <Readout label="browser">
            {browser === null ? (
              <Absent>unrecognised</Absent>
            ) : (
              `${browser.name} ${browser.version}`
            )}
          </Readout>
          <Readout label="negotiated">
            {capability.tag === "supported" ? (
              capability.mime
            ) : (
              <span className="text-destructive">
                unsupported: {capability.reason}
              </span>
            )}
          </Readout>
          <Readout label="recorder">
            {recording?.mime ?? <Absent>record something first</Absent>}
          </Readout>
          <Readout label="duration">
            {recording === null ? (
              <Absent />
            ) : (
              `${String(recording.durationMs)} ms`
            )}
          </Readout>
          <Readout label="bytes">
            {recording === null ? (
              <Absent />
            ) : (
              `${recording.bytes.toLocaleString("en-US")} (${bytes(recording.bytes)})`
            )}
          </Readout>
          <Readout label="bytes/min">
            {bpm === null ? <Absent /> : bpm.toLocaleString("en-US")}
          </Readout>
          <Readout label="user agent">
            <span className="text-xs">{report.userAgent}</span>
          </Readout>
        </Readouts>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reprompt" className="lab-label">
              Re-prompt after denial
            </Label>
            <select
              id="reprompt"
              value={reprompt}
              onChange={(e) => {
                if (isReprompt(e.target.value)) setReprompt(e.target.value);
                setCopy("idle");
              }}
              className="border-input bg-card h-9 rounded-sm border px-2 font-mono text-xs"
            >
              {REPROMPTS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <p className="text-muted-foreground text-xs">
              Deny the prompt once, tap Ask again, and say what you saw. Only
              you can see a permission prompt.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="notes" className="lab-label">
              Notes
            </Label>
            <Input
              id="notes"
              value={notes}
              placeholder="device, OS version, anything odd"
              onChange={(e) => {
                setNotes(e.target.value);
                setCopy("idle");
              }}
              className="text-xs"
            />
          </div>
        </div>
      </div>
      <pre
        className="lab-readout bg-background/60 rounded-sm border p-2 text-xs break-all whitespace-pre-wrap select-all"
        aria-label="Report text"
      >
        {text}
      </pre>
      <p className="text-muted-foreground text-xs">
        The browser is a guess from the user agent — correct it before pasting.
        An iPad asking for desktop sites reports itself as Safari macOS.
      </p>
    </Panel>
  );
}
